import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { FormProvider, RHFInput, RHFPassword } from 'ui/utils/form';
import { Box, Button, Link as MuiLink, Typography } from 'ui';
import { Header, Main } from '../../layout';
import useAuth from '../../hooks/useAuth';
import { LOGIN_ROUTES, EULA_LINK } from '../../constants';
import palette from 'theme/palette';
import { TypographyLink } from 'pages/endpoints/components/TypographyLink';

type FormValuesProps = {
  email: string;
  password: string;
};

const schema = object({
  email: string().required(),
  password: string().required()
}).required();

const FlexContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
}));

const LinkStyled = styled(Link)(() => ({
  textDecoration: 'none'
}));

const LoginForm = () => {
  const { login, loading, error } = useAuth();

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: FormValuesProps) => {
    void login({ ...data });
  };

  return (
    <FormProvider fullHeight onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <Main style={{ marginTop: '38vh', gap: '3px' }}>
        <Header title="Sign in" />

        <RHFInput
          name="email"
          type="email"
          autoComplete="email"
          placeholder="Email address"
          autoFocus={true}
          dataTestId="login-email"
          size="small"
          style={{ minHeight: '62px' }}
        />

        <RHFPassword name="password" style={{ minHeight: '62px' }} />
        <FlexContainer>
          <LinkStyled to={LOGIN_ROUTES.FORGOT_PASSWORD}>
            <TypographyLink
              hasLink
              display="flex"
              justifyContent="start"
              color={palette.primary.link}
              variant="subtitle2"
            >
              Forgot password?
            </TypographyLink>
          </LinkStyled>
        </FlexContainer>

        <FlexContainer>
          <Button type="submit" formNoValidate loading={loading} dataTestId="login-submit" sx={{ marginTop: 2 }}>
            Login
          </Button>

          <Typography display="flex" justifyContent="start" variant="body2">
            By clicking Login, you agree to Cynet's{' '}
            <MuiLink variant="body2" ml="3px" underline="hover" target="_blank" href={EULA_LINK}>
              Terms & Conditions
            </MuiLink>
          </Typography>
        </FlexContainer>

        {error && (
          <Typography color="error" variant="subtitle2" sx={{ margin: 'auto' }}>
            {error}
          </Typography>
        )}
      </Main>
    </FormProvider>
  );
};

export default LoginForm;
